if (!customElements.get('nau-azazie-variant-picker')) {
  customElements.define(
    'nau-azazie-variant-picker',
    class NauAzazieVariantPicker extends HTMLElement {
      constructor() {
        super();
        this.handleChange = this.handleChange.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
      }

      connectedCallback() {
        this.variantSelects = this.closest('variant-selects');
        if (!this.variantSelects) return;

        this.dataset.pickerJsVersion = '3.4';

        this.colorToggle = this.querySelector('[data-color-toggle]');
        this.colorPanel = this.querySelector('[data-color-panel]');
        this.colorSearch = this.querySelector('[data-color-search]');
        this.colorList = this.querySelector('[data-color-list]');
        this.colorSwatchGrid = this.querySelector('[data-color-swatch-grid]');
        this.colorNoResults = this.querySelector('[data-color-no-results]');
        this.statusWrapper = this.querySelector('[data-color-status-wrapper]');
        this.statusButton = this.querySelector('[data-color-status-button]');
        this.statusTooltip = this.querySelector('[data-color-status-tooltip]');

        this.ensureColorSwatchGrid();
        this.buildColorSwatches();

        this.colorToggle?.addEventListener('click', () => {
          this.setColorPanelOpen(this.colorPanel?.hidden ?? true);
        });

        this.colorSearch?.addEventListener('input', () => this.filterColors());
        this.statusButton?.addEventListener('click', (event) => this.toggleStatusTooltip(event));
        this.addEventListener('click', this.handleClick);
        this.addEventListener('change', this.handleChange);
        this.addEventListener('keydown', this.handleKeydown);
        document.addEventListener('click', this.handleDocumentClick);

        this.variantData = this.getJson('[data-azazie-variant-data]', []);
        this.variantMetafields = this.getJson('[data-azazie-variant-metafields]', {});

        this.syncAvailability();
        this.syncSelectedColor();
        this.syncColorStatus();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('change', this.handleChange);
        this.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('click', this.handleDocumentClick);
      }

      getJson(selector, fallback) {
        const element = this.variantSelects.querySelector(selector);
        if (!element) return fallback;

        try {
          return JSON.parse(element.textContent);
        } catch (error) {
          console.error(`Unable to parse ${selector}`, error);
          return fallback;
        }
      }

      ensureColorSwatchGrid() {
        const colorGroup = this.querySelector('[data-color-option-group]');
        if (!colorGroup) return;

        let dropdown = this.querySelector('.nau-azazie-picker__color-dropdown');
        if (!dropdown) {
          const selectedRow = this.querySelector('.nau-azazie-picker__selected-row');
          const colorPanel = this.querySelector('[data-color-panel]');

          if (selectedRow) {
            dropdown = document.createElement('div');
            dropdown.className = 'nau-azazie-picker__color-dropdown';
            selectedRow.before(dropdown);
            dropdown.append(selectedRow);
            if (colorPanel) dropdown.append(colorPanel);
          }
        }

        if (!this.colorSwatchGrid) {
          const grid = document.createElement('div');
          grid.className = 'nau-azazie-picker__swatch-grid';
          grid.setAttribute('role', 'radiogroup');
          grid.setAttribute('aria-label', 'Color');
          grid.dataset.colorSwatchGrid = '';
          grid.style.display = 'flex';
          grid.style.flexWrap = 'wrap';
          grid.style.gap = '0.9rem';
          grid.style.marginTop = '1.2rem';
          grid.style.border = '0';
          colorGroup.append(grid);
          this.colorSwatchGrid = grid;
        }

        this.colorSwatchGrid.style.display = 'flex';
        this.colorSwatchGrid.style.flexWrap = 'wrap';
        this.colorSwatchGrid.style.gap = '0.9rem';
        this.colorSwatchGrid.style.marginTop = '1.2rem';
        this.colorSwatchGrid.style.border = '0';
      }

      buildColorSwatches() {
        if (!this.colorSwatchGrid) return;

        const renderedSwatches = Array.from(this.colorSwatchGrid.querySelectorAll('[data-color-swatch]'));
        if (renderedSwatches.length) {
          this.colorSwatchGrid.dataset.colorSwatchCount = String(renderedSwatches.length);
          return;
        }

        const colorItems = Array.from(this.querySelectorAll('[data-color-item]'));
        const fragment = document.createDocumentFragment();

        let swatchCount = 0;

        colorItems.forEach((item) => {
          const input = this.getInputForColorControl(item);
          const sourceSwatch = item.querySelector('.nau-azazie-picker__swatch');
          const colorName = input?.value || item.querySelector('[data-color-name-text]')?.textContent.trim() || '';
          const selected = item.getAttribute('aria-selected') === 'true';
          const unavailable = item.getAttribute('aria-disabled') === 'true' || input?.disabled;

          if (!input || !sourceSwatch) return;

          const button = document.createElement('button');
          button.type = 'button';
          button.className = `nau-azazie-picker__grid-swatch${
            item.classList.contains('nau-azazie-picker__color-item--rectangle')
              ? ' nau-azazie-picker__grid-swatch--rectangle'
              : ''
          }`;
          button.setAttribute('role', 'radio');
          button.setAttribute('aria-checked', String(selected));
          button.setAttribute(
            'aria-label',
            `${colorName}${selected ? ', selected' : ', not selected'}${unavailable ? ', unavailable' : ''}`
          );
          button.tabIndex = selected ? 0 : -1;
          button.dataset.colorSwatch = '';
          button.dataset.colorTargetId = input.id;
          button.dataset.colorName = item.dataset.colorName || colorName.toLowerCase();
          button.disabled = Boolean(unavailable);
          button.toggleAttribute('aria-disabled', Boolean(unavailable));
          button.classList.toggle('is-unavailable', Boolean(unavailable));
          button.hidden = item.hidden;
          button.style.display = 'inline-grid';
          button.style.width = item.classList.contains('nau-azazie-picker__color-item--rectangle')
            ? '4.8rem'
            : '3.8rem';
          button.style.minWidth = button.style.width;
          button.style.height = '3.8rem';
          button.style.placeItems = 'center';
          button.style.padding = '0.35rem';
          button.style.border = `0.1rem solid ${selected ? 'currentColor' : 'transparent'}`;
          button.style.borderRadius = item.classList.contains('nau-azazie-picker__color-item--rectangle')
            ? '0.3rem'
            : '50%';
          button.style.background = 'transparent';
          button.style.opacity = unavailable ? '0.38' : '1';

          const chip = sourceSwatch.cloneNode(false);
          chip.className = 'nau-azazie-picker__grid-swatch-chip nau-azazie-picker__swatch';
          chip.setAttribute('aria-hidden', 'true');

          const tooltip = document.createElement('span');
          tooltip.className = 'nau-azazie-picker__grid-swatch-tooltip';
          tooltip.setAttribute('aria-hidden', 'true');
          tooltip.textContent = colorName;

          button.append(chip, tooltip);
          fragment.append(button);
          swatchCount += 1;
        });

        this.colorSwatchGrid.replaceChildren(fragment);
        this.colorSwatchGrid.dataset.colorSwatchCount = String(swatchCount);
      }

      handleChange(event) {
        if (!event.target.matches('[data-azazie-option-input], [data-azazie-select]')) return;

        queueMicrotask(() => {
          this.syncAvailability();
          this.syncSelectedColor();
          this.syncColorStatus();

          if (event.target.matches('[data-color-input]')) {
            this.setColorPanelOpen(false);
          }
        });
      }

      handleClick(event) {
        const swatch = event.target.closest('[data-color-swatch]');
        if (!swatch || !this.contains(swatch)) return;

        // Server-rendered swatches are labels, so the browser activates their
        // associated radio input without JavaScript.
        if (swatch.tagName === 'LABEL') return;

        this.activateColorControl(swatch);
      }

      handleKeydown(event) {
        const isSearch = event.target.matches('[data-color-search]');
        const isColorItem = event.target.matches('[data-color-item]');
        const isColorSwatch = event.target.matches('[data-color-swatch]');

        if ((isSearch || isColorItem) && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          this.moveColorFocus(event.key, event.target);
          return;
        }

        if (
          isColorSwatch &&
          ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)
        ) {
          event.preventDefault();
          this.moveSwatchFocus(event.key, event.target);
          return;
        }

        if ((isColorItem || isColorSwatch) && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          this.activateColorControl(event.target, isColorItem);
          return;
        }

        if (event.key !== 'Escape') return;

        if (this.statusTooltip && !this.statusTooltip.hidden) {
          this.setStatusTooltipOpen(false);
          this.statusButton?.focus();
          return;
        }

        if (this.colorPanel && !this.colorPanel.hidden) {
          this.setColorPanelOpen(false);
          this.colorToggle?.focus();
        }
      }

      handleDocumentClick(event) {
        if (this.statusTooltip && !this.statusTooltip.hidden) {
          if (event.target.closest('[data-color-status-wrapper]') !== this.statusWrapper) {
            this.setStatusTooltipOpen(false);
          }
        }

        if (this.colorPanel && !this.colorPanel.hidden) {
          const colorGroup = this.querySelector('[data-color-option-group]');
          if (!colorGroup?.contains(event.target)) this.setColorPanelOpen(false);
        }
      }

      setColorPanelOpen(open) {
        if (!this.colorPanel || !this.colorToggle) return;

        this.colorPanel.hidden = !open;
        this.colorToggle.setAttribute('aria-expanded', String(open));
        const selectedColor = this.querySelector('[data-selected-color-name]')?.textContent.trim() || '';
        this.colorToggle.setAttribute(
          'aria-label',
          `Color selection, ${open ? 'expanded' : 'collapsed'}, selected ${selectedColor}`
        );

        if (open) {
          requestAnimationFrame(() => {
            if (this.colorSearch) {
              this.colorSearch.focus();
            } else {
              this.getVisibleColorItems()[0]?.focus();
            }
          });
        }

        if (!open && this.colorSearch) {
          this.colorSearch.value = '';
          this.filterColors();
        }
      }

      filterColors() {
        if (!this.colorSearch) return;

        const query = this.colorSearch.value.trim().toLowerCase();
        let visibleCount = 0;

        this.querySelectorAll('[data-color-item]').forEach((item) => {
          const hiddenByAvailability = this.dataset.hideSoldOut === 'true' && item.classList.contains('is-unavailable');
          const matches = (!query || item.dataset.colorName.includes(query)) && !hiddenByAvailability;
          item.hidden = !matches;
          if (matches) visibleCount += 1;
        });

        if (this.colorNoResults) this.colorNoResults.hidden = visibleCount !== 0;
      }

      getVisibleColorItems() {
        return Array.from(this.querySelectorAll('[data-color-item]')).filter(
          (item) => !item.hidden && item.getAttribute('aria-disabled') !== 'true'
        );
      }

      moveColorFocus(key, currentTarget) {
        const items = this.getVisibleColorItems();
        if (!items.length) return;

        if (key === 'Home') {
          items[0].focus();
          return;
        }

        if (key === 'End') {
          items[items.length - 1].focus();
          return;
        }

        const currentIndex = items.indexOf(currentTarget);
        if (currentIndex === -1) {
          (key === 'ArrowUp' ? items[items.length - 1] : items[0]).focus();
          return;
        }

        const offset = key === 'ArrowUp' ? -1 : 1;
        const nextIndex = (currentIndex + offset + items.length) % items.length;
        items[nextIndex].focus();
      }

      getAvailableColorSwatches() {
        return Array.from(this.querySelectorAll('[data-color-swatch]')).filter(
          (swatch) => !swatch.hidden && swatch.getAttribute('aria-disabled') !== 'true'
        );
      }

      moveSwatchFocus(key, currentTarget) {
        const swatches = this.getAvailableColorSwatches();
        if (!swatches.length) return;

        let nextSwatch;
        if (key === 'Home') {
          nextSwatch = swatches[0];
        } else if (key === 'End') {
          nextSwatch = swatches[swatches.length - 1];
        } else {
          const currentIndex = swatches.indexOf(currentTarget);
          const moveBackward = key === 'ArrowUp' || key === 'ArrowLeft';
          const offset = moveBackward ? -1 : 1;
          const startIndex = currentIndex === -1 ? 0 : currentIndex;
          nextSwatch = swatches[(startIndex + offset + swatches.length) % swatches.length];
        }

        nextSwatch?.focus();
      }

      getInputForColorControl(control) {
        const inputId = control.dataset.colorTargetId || control.htmlFor;
        return Array.from(this.querySelectorAll('[data-color-input]')).find((input) => input.id === inputId);
      }

      activateColorControl(control, returnFocusToToggle = false) {
        const input = this.getInputForColorControl(control);
        if (!input || input.disabled) return;

        input.click();
        if (returnFocusToToggle) this.colorToggle?.focus();
      }

      getSelectedValues() {
        return Array.from(this.variantSelects.querySelectorAll('[data-azazie-option-group]')).map((group) => {
          const select = group.querySelector('select');
          if (select) return select.value;
          return group.querySelector('input[type="radio"]:checked')?.value;
        });
      }

      findCurrentVariant() {
        if (this.variantSelects.currentVariant) return this.variantSelects.currentVariant;

        const selectedValues = this.getSelectedValues();
        return this.variantData.find((variant) =>
          variant.options.every((value, index) => selectedValues[index] === value)
        );
      }

      isValueAvailable(position, value, selectedValues) {
        return this.variantData.some((variant) => {
          if (!variant.available || variant.options[position - 1] !== value) return false;

          for (let index = 0; index < position - 1; index += 1) {
            if (variant.options[index] !== selectedValues[index]) return false;
          }

          return true;
        });
      }

      syncAvailability() {
        if (!this.variantData.length) return;

        const selectedValues = this.getSelectedValues();
        const hideSoldOut = this.dataset.hideSoldOut === 'true';

        this.querySelectorAll('[data-azazie-option-group]').forEach((group) => {
          const position = Number(group.dataset.optionPosition);
          if (!position) return;

          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            const available = this.isValueAvailable(position, input.value, selectedValues);
            const controls = input.matches('[data-color-input]')
              ? Array.from(this.querySelectorAll('[data-color-label-for], [data-color-target-id]')).filter(
                  (control) =>
                    (control.dataset.colorLabelFor || control.dataset.colorTargetId) === input.id
                )
              : [input.nextElementSibling?.matches('label') ? input.nextElementSibling : null].filter(Boolean);

            input.classList.toggle('disabled', !available);
            input.disabled = !available;

            controls.forEach((control) => {
              control.toggleAttribute('aria-disabled', !available);
              control.classList.toggle('is-unavailable', !available);
              control.hidden = hideSoldOut && !available;
              if (control.matches('button')) {
                control.disabled = !available;
                control.style.opacity = available ? '1' : '0.38';
              }
            });
          });

          group.querySelectorAll('option').forEach((option) => {
            const available = this.isValueAvailable(position, option.value, selectedValues);
            const label = option.dataset.baseLabel || option.value;

            option.disabled = !available;
            option.hidden = hideSoldOut && !available;
            option.textContent = available ? label : this.getUnavailableLabel(label);
          });
        });

        if (this.colorSearch) this.filterColors();
      }

      getUnavailableLabel(value) {
        const template = window.variantStrings?.unavailable_with_option;
        if (template) return template.replace('[value]', value);
        return `${value} — Unavailable`;
      }

      syncSelectedColor() {
        const selectedInput = this.querySelector('[data-color-input]:checked');
        const selectedName = this.querySelector('[data-selected-color-name]');
        const selectedSwatch = this.querySelector('[data-selected-color-swatch]');

        if (!selectedInput) return;
        if (selectedName) selectedName.textContent = selectedInput.value;

        this.querySelectorAll('[data-color-item]').forEach((item) => {
          const selected = item.htmlFor === selectedInput.id;
          item.setAttribute('aria-selected', String(selected));
          item.setAttribute(
            'aria-label',
            `${item.querySelector('[data-color-name-text]')?.textContent.trim() || selectedInput.value}${
              selected ? ', selected' : ''
            }${item.getAttribute('aria-disabled') === 'true' ? ', unavailable' : ''}`
          );
        });

        this.querySelectorAll('[data-color-swatch]').forEach((swatch) => {
          const selected = swatch.dataset.colorTargetId === selectedInput.id;
          const colorName = this.getInputForColorControl(swatch)?.value || selectedInput.value;
          const unavailable = swatch.getAttribute('aria-disabled') === 'true';

          swatch.setAttribute('aria-checked', String(selected));
          swatch.setAttribute(
            'aria-label',
            `${colorName}${selected ? ', selected' : ', not selected'}${unavailable ? ', unavailable' : ''}`
          );
          swatch.tabIndex = selected ? 0 : -1;
          swatch.style.borderColor = selected ? 'currentColor' : 'transparent';
        });

        if (selectedSwatch) {
          selectedSwatch.style.setProperty(
            '--nau-azazie-selected-background',
            selectedInput.dataset.swatchBackground || '#e5e5e5'
          );
          selectedSwatch.style.setProperty(
            '--nau-azazie-selected-focal-point',
            selectedInput.dataset.swatchFocalPoint || 'center'
          );
        }
      }

      syncColorStatus() {
        if (!this.statusWrapper) return;

        const currentVariant = this.findCurrentVariant();
        const status = currentVariant ? this.variantMetafields[String(currentVariant.id)] || '' : '';
        const text = this.querySelector('[data-color-status-text]');
        const title = this.querySelector('[data-color-status-title]');
        const description = this.querySelector('[data-color-status-description]');

        const content = {
          Core: {
            label: 'Core',
            title: 'Core Colors',
            description:
              "Our signature shades, designed to last and restocked season after season. Can't see your size? Reach out to get the latest update on restocks.",
          },
          Special: {
            label: 'Special',
            title: 'Special Edition',
            description: 'Limited production, available while quantities last. Contact us about made-to-order color options.',
          },
        }[status];

        if (!content) {
          this.statusWrapper.style.display = 'none';
          this.setStatusTooltipOpen(false);
          return;
        }

        if (text) text.textContent = `— ${content.label}`;
        if (title) title.textContent = content.title;
        if (description) description.textContent = content.description;
        this.statusWrapper.style.display = 'inline-flex';
      }

      toggleStatusTooltip(event) {
        event.stopPropagation();
        this.setStatusTooltipOpen(this.statusTooltip?.hidden ?? true);
      }

      setStatusTooltipOpen(open) {
        if (!this.statusTooltip || !this.statusButton) return;
        this.statusTooltip.hidden = !open;
        this.statusButton.setAttribute('aria-expanded', String(open));
      }
    }
  );
}
